import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, Upload, X, MapPin, Clock,
  CheckCircle2, Loader2, PhoneCall, ShieldCheck, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import SeoHead from "../components/SeoHead";
import Icon from "../components/Icon";
import { LOCALITIES, SLOTS } from "../data/services";
import { useServices } from "../context/ServicesContext";
import { whatsappLink } from "../data/content";
import { api, uploadFile, formatApiErrorDetail } from "../lib/api";
import { getFirebaseAuth, firebaseEnabled, RecaptchaVerifier, signInWithPhoneNumber } from "../lib/firebase";

const STEP_LABELS = ["Service", "Location", "Problem", "Schedule", "Contact"];

export default function BookingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const prefill = location.state?.prefill;
  const { services } = useServices();
  const slugParam = params.get("service");

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [idToken, setIdToken] = useState("");
  const recaptchaRef = useRef(null);
  const confirmationRef = useRef(null);

  const [form, setForm] = useState({
    service: null,
    locality: params.get("locality") || "",
    address: "",
    pincode: "",
    landmark: "",
    problem: params.get("q") || "",
    photos: [], // {id, filename}
    date: "",
    slot: "",
    name: "",
    phone: "",
    email: "",
  });

  // One-tap rebook: prefill from a past booking passed via router state.
  const prefillDone = useRef(false);
  useEffect(() => {
    if (prefill && !prefillDone.current) {
      prefillDone.current = true;
      setForm((f) => ({
        ...f,
        service: prefill.service || f.service,
        locality: prefill.locality || f.locality,
        address: prefill.address || f.address,
        pincode: prefill.pincode || f.pincode,
        landmark: prefill.landmark || f.landmark,
        problem: prefill.problem || f.problem,
      }));
      setStep(prefill.service ? 1 : 0);
    }
  }, [prefill]);

  useEffect(() => {
    if (slugParam && !form.service && services.length) {
      const found = services.find((s) => s.slug === slugParam);
      if (found) {
        setForm((f) => ({ ...f, service: found }));
        setStep(1);
      }
    }
  }, [services, slugParam, form.service]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toE164 = () => {
    const d = form.phone.replace(/\D/g, "");
    return d.length > 10 ? `+${d}` : `+91${d}`;
  };

  const resetRecaptcha = () => {
    if (recaptchaRef.current) { try { recaptchaRef.current.clear(); } catch { /* noop */ } }
    recaptchaRef.current = null;
    confirmationRef.current = null;
  };

  const firebaseOtpError = (err) => {
    const code = err?.code || "";
    if (code === "auth/unauthorized-domain") return "This website isn't authorized for OTP yet. Please contact support.";
    if (code === "auth/billing-not-enabled") return "SMS OTP is temporarily unavailable. Please try again later.";
    if (code === "auth/invalid-phone-number") return "Please enter a valid mobile number.";
    if (code === "auth/too-many-requests") return "Too many attempts. Please wait a few minutes and try again.";
    if (code === "auth/invalid-verification-code" || code === "auth/code-expired") return "Incorrect or expired OTP. Please request a new one.";
    return (err?.message || "").replace("Firebase: ", "").replace(/\s*\(auth\/.*\)\.?/, "") || "Could not send OTP. Please try again.";
  };

  const sendOtp = async () => {
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone)) { toast.error("Enter a valid mobile number first"); return; }
    setSendingOtp(true);
    try {
      if (firebaseEnabled) {
        const auth = getFirebaseAuth();
        if (!recaptchaRef.current) {
          recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
        }
        confirmationRef.current = await signInWithPhoneNumber(auth, toE164(), recaptchaRef.current);
        setOtpSent(true);
        toast.success("OTP sent to your mobile via SMS");
      } else {
        const { data } = await api.post("/otp/send", { phone: form.phone });
        setOtpSent(true);
        toast.success(`Demo OTP sent: ${data.demo_otp}`, { duration: 8000 });
      }
    } catch (err) {
      resetRecaptcha();
      toast.error(firebaseOtpError(err));
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    try {
      if (firebaseEnabled) {
        const cred = await confirmationRef.current.confirm(otpCode);
        const token = await cred.user.getIdToken();
        setIdToken(token);
        setOtpVerified(true);
        toast.success("Mobile number verified!");
      } else {
        await api.post("/otp/verify", { phone: form.phone, code: otpCode });
        setOtpVerified(true);
        toast.success("Mobile number verified!");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || firebaseOtpError(err));
    }
  };

  const canNext = useMemo(() => {
    if (step === 0) return !!form.service;
    if (step === 1) return form.locality && form.address.trim().length > 3 && /^\d{6}$/.test(form.pincode);
    if (step === 2) return form.problem.trim().length > 5;
    if (step === 3) return !!form.slot;
    if (step === 4) return form.name.trim() && /^[0-9+\-\s]{10,15}$/.test(form.phone);
    return false;
  }, [step, form]);

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadFile(file);
        setForm((f) => ({ ...f, photos: [...f.photos, { id: res.id, filename: res.filename }] }));
      }
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (id) => set("photos", form.photos.filter((p) => p.id !== id));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_id: form.service.serviceId,
        service_name: form.service.name,
        category_id: form.service.id,
        problem_description: form.problem,
        locality: form.locality,
        address: form.address,
        landmark: form.landmark,
        preferred_date: form.date,
        preferred_slot: SLOTS.find((s) => s.id === form.slot)?.label || form.slot,
        customer_name: form.name,
        customer_phone: form.phone,
        customer_email: form.email,
        pincode: form.pincode,
        photo_ids: form.photos.map((p) => p.id),
        phone_verified: otpVerified,
        firebase_id_token: idToken,
      });
      setConfirmation(data.booking);
      toast.success("Booking confirmed!");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <SeoHead title="Booking Confirmed - ApnaFix" />
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center af-fade-up" data-testid="booking-confirmation">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 mx-auto"><CheckCircle2 className="w-9 h-9" /></span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">Booking Confirmed!</h1>
          <p className="mt-2 text-slate-600">We're matching you with a verified local professional for <span className="font-semibold text-navy">{confirmation.service_name}</span> in {confirmation.locality}.</p>
          <div className="mt-6 text-left bg-slate-50 rounded-2xl p-5 space-y-2 text-sm">
            <p className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono-num font-semibold text-slate-900">{confirmation.ref}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Service</span><span className="font-medium text-slate-900">{confirmation.service_name}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">When</span><span className="font-medium text-slate-900">{confirmation.preferred_slot || "To be scheduled"}</span></p>
            <p className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-medium text-amber-600">{String(confirmation.status || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) || "Received"}</span></p>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Button data-testid="booking-track-btn" onClick={() => navigate(`/track/${confirmation.ref}`)} className="h-11 px-5 bg-brand-orange hover:bg-orange-600 text-white font-semibold">Track my booking</Button>
            <a href={whatsappLink(`Hi ApnaFix, I just booked ${confirmation.service_name} (ID ${confirmation.ref}).`)} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 px-5">
              <PhoneCall className="w-4 h-4" /> Chat on WhatsApp
            </a>
            <Button variant="outline" onClick={() => navigate("/")} className="h-11 px-5 font-semibold">Back to Home</Button>
          </div>
          <p className="mt-4 text-xs text-slate-400">Save your Booking ID <span className="font-mono-num font-bold text-slate-600">{confirmation.ref}</span> to track your booking status.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <SeoHead title="Book a Local Service - ApnaFix" description="Book a verified local professional for AC repair, electrician, plumber and more." />
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900">Book a Service Professional</h1>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-1 sm:gap-2">
        {STEP_LABELS.map((lbl, i) => (
          <div key={lbl} className="flex-1 flex items-center gap-1 sm:gap-2">
            <div className={`flex items-center gap-2 ${i <= step ? "text-navy" : "text-slate-400"}`}>
              <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-400"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </span>
              <span className="hidden sm:inline text-xs font-semibold">{lbl}</span>
            </div>
            {i < STEP_LABELS.length - 1 && <div className={`flex-1 h-0.5 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8 min-h-[340px]">
        {/* STEP 0: Service */}
        {step === 0 && (
          <div className="af-fade-up">
            <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Which service do you need?</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {services.map((s) => (
                <button key={s.id} data-testid={`booking-service-${s.slug}`} onClick={() => { set("service", s); setStep(1); }}
                        className={`text-left rounded-xl border p-3 transition-all ${form.service?.id === s.id ? "border-brand-orange ring-2 ring-orange-200" : "border-slate-200 hover:border-orange-300"}`}>
                  <span className="grid place-items-center w-10 h-10 rounded-lg bg-orange-50 text-brand-orange"><Icon name={s.icon} className="w-5 h-5" /></span>
                  <p className="mt-2 text-sm font-semibold text-slate-900 leading-snug">{s.name}</p>
                  <p className="text-xs text-slate-500 font-mono-num">{s.priceStarts}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 1: Location */}
        {step === 1 && (
          <div className="af-fade-up space-y-4">
            <h2 className="font-display font-bold text-lg text-slate-900">Where do you need the service?</h2>
            <div>
              <Label className="text-sm">Locality</Label>
              <Select value={form.locality} onValueChange={(v) => set("locality", v)}>
                <SelectTrigger data-testid="booking-locality-select" className="mt-1.5 h-11"><SelectValue placeholder="Select your area" /></SelectTrigger>
                <SelectContent>{LOCALITIES.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Full address (flat / wing / building)</Label>
              <Textarea data-testid="booking-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="e.g. A-1203, Sunrise Towers, Mahavir Nagar" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm">Pincode</Label>
              <Input data-testid="booking-pincode-input" value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit pincode" inputMode="numeric" maxLength={6} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-sm">Landmark (optional)</Label>
              <Input data-testid="booking-landmark-input" value={form.landmark} onChange={(e) => set("landmark", e.target.value)} placeholder="Near City Mall" className="mt-1.5 h-11" />
            </div>
          </div>
        )}

        {/* STEP 2: Problem + photos */}
        {step === 2 && (
          <div className="af-fade-up space-y-4">
            <h2 className="font-display font-bold text-lg text-slate-900">Describe the problem</h2>
            <Textarea data-testid="booking-problem-input" value={form.problem} onChange={(e) => set("problem", e.target.value)} rows={4} placeholder="Tell us what's wrong, e.g. AC not cooling, making noise since 2 days..." />
            <div>
              <Label className="text-sm">Add photos (optional)</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {form.photos.map((p) => (
                  <div key={p.id} className="relative w-20 h-20 rounded-xl bg-slate-100 border border-slate-200 grid place-items-center text-[10px] text-slate-500 p-1 text-center overflow-hidden">
                    <span className="line-clamp-2">{p.filename}</span>
                    <button onClick={() => removePhoto(p.id)} className="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-red-500 text-white"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label data-testid="booking-photo-upload" className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 grid place-items-center cursor-pointer hover:border-brand-orange text-slate-400 hover:text-brand-orange">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Schedule */}
        {step === 3 && (
          <div className="af-fade-up space-y-4">
            <h2 className="font-display font-bold text-lg text-slate-900">Pick a convenient time</h2>
            <div>
              <Label className="text-sm">Preferred date</Label>
              <Input data-testid="booking-date-input" type="date" value={form.date} min={new Date().toISOString().split("T")[0]} onChange={(e) => set("date", e.target.value)} className="mt-1.5 h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {SLOTS.map((s) => (
                <button key={s.id} data-testid={`booking-slot-${s.id}`} onClick={() => set("slot", s.id)}
                        className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-medium transition-all ${form.slot === s.id ? "border-brand-orange bg-orange-50 text-orange-900 ring-2 ring-orange-200" : "border-slate-200 text-slate-700 hover:border-orange-300"}`}>
                  <Clock className="w-4 h-4 text-brand-orange" /> {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 4: Contact */}
        {step === 4 && (
          <div className="af-fade-up space-y-4">
            <h2 className="font-display font-bold text-lg text-slate-900">Your contact details</h2>
            <div>
              <Label className="text-sm">Full name</Label>
              <Input data-testid="booking-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-sm">Mobile number</Label>
              <div className="mt-1.5 flex gap-2">
                <Input data-testid="booking-phone-input" value={form.phone} disabled={otpVerified}
                       onChange={(e) => { set("phone", e.target.value); setOtpSent(false); setOtpVerified(false); setOtpCode(""); setIdToken(""); resetRecaptcha(); }}
                       placeholder="10-digit mobile" className="h-11" />
                {!otpVerified && (
                  <Button type="button" data-testid="booking-send-otp-btn" variant="outline" onClick={sendOtp} disabled={sendingOtp} className="h-11 whitespace-nowrap font-semibold">
                    {sendingOtp ? <Loader2 className="w-4 h-4 animate-spin" /> : otpSent ? "Resend" : "Send OTP"}
                  </Button>
                )}
              </div>
              {otpVerified ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600"><ShieldCheck className="w-4 h-4" /> Mobile verified</p>
              ) : otpSent ? (
                <div className="mt-2 flex gap-2">
                  <div className="relative flex-1">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input data-testid="booking-otp-input" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} placeholder="Enter 6-digit OTP" className="h-11 pl-9 font-mono-num" maxLength={6} />
                  </div>
                  <Button type="button" data-testid="booking-verify-otp-btn" onClick={verifyOtp} disabled={otpCode.length < 4} className="h-11 bg-navy hover:bg-navy-900 text-white font-semibold">Verify</Button>
                </div>
              ) : null}
            </div>
            <div id="recaptcha-container" />
            <div>
              <Label className="text-sm">Email (optional)</Label>
              <Input data-testid="booking-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="mt-1.5 h-11" />
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm flex items-start gap-3">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-navy text-white shrink-0"><Icon name={form.service?.icon || "Wrench"} className="w-4 h-4" /></span>
              <div>
                <p className="font-semibold text-slate-900">{form.service?.name} • <MapPin className="w-3 h-3 inline" /> {form.locality}</p>
                <p className="text-slate-500 text-xs mt-0.5">{SLOTS.find((s) => s.id === form.slot)?.label}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nav buttons */}
      <div className="mt-6 flex items-center justify-between gap-3">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="font-semibold">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        {step < 4 ? (
          <Button data-testid="booking-modal-next-btn" disabled={!canNext} onClick={() => setStep((s) => s + 1)} className="bg-brand-orange hover:bg-orange-600 text-white font-semibold px-6">
            Continue <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button data-testid="booking-modal-confirm-btn" disabled={!canNext || submitting || !otpVerified} onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Confirming...</> : !otpVerified ? "Verify mobile to confirm" : <>Confirm Booking <Check className="w-4 h-4 ml-1" /></>}
          </Button>
        )}
      </div>
    </div>
  );
}
